import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Gift, Clover, Clock, CalendarDays, Loader2, RefreshCw,
  FileText, Star, Rocket, ExternalLink,
  TrendingUp, BookOpen, Briefcase, Megaphone, Pin,
} from 'lucide-react';
import ClientHostEvents from './ClientHostEvents';
import HostStarPanel from './host/HostStarPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtMinutes = (minutes) => {
  const h = Math.floor((minutes || 0) / 60);
  const m = (minutes || 0) % 60;
  return h > 0 ? `${h}h ${m}min` : `${m}min`;
};
const fmtNum = (n) => Number(n || 0).toLocaleString('pt-BR');

const NOTICE_HTML_CLS = 'text-sm text-slate-700 leading-relaxed break-words ' +
  '[&_a]:text-blue-600 [&_a]:underline [&_a]:underline-offset-2 ' +
  '[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:min-h-[1em]';

const fmtNoticeDate = (iso) => {
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit',
      year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return '-'; }
};

const POLICIES = [
  {
    id: 'pagamentos',
    title: 'Política de Hosts — Pagamentos',
    desc: 'Comissões, metas, remuneração e regras para saque.',
    file: '/host-docs/politica-de-hosts.pdf',
    icon: FileText,
    accent: 'from-orange-400 to-orange-600 shadow-orange-200',
  },
  {
    id: 'star-host',
    title: 'Política de STAR HOST',
    desc: 'Como se tornar Star Host, bonificações, níveis e regras.',
    file: '/host-docs/politica-star-host.pdf',
    icon: Star,
    accent: 'from-yellow-400 to-amber-500 shadow-amber-200',
  },
  {
    id: 'integracao',
    title: 'Apoio à Integração de Novos Hosts',
    desc: 'Programa de apoio para quem está começando na agência.',
    file: '/host-docs/politica-integracao-novos-hosts.pdf',
    icon: Rocket,
    accent: 'from-sky-400 to-blue-600 shadow-sky-200',
  },
];

const StatCard = ({ icon: Icon, label, value, accent, testId, sub }) => (
  <Card className="bg-white/80 backdrop-blur-xl border border-white/60 shadow-[0_8px_32px_rgba(124,58,237,0.08)] rounded-2xl" data-testid={testId}>
    <CardContent className="p-3 sm:p-4 flex items-center gap-2.5 sm:gap-3.5">
      <div className={`w-9 h-9 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center bg-gradient-to-br shadow-md shrink-0 ${accent}`}>
        <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
      </div>
      <div className="min-w-0">
        <p className="text-[9px] sm:text-[11px] font-bold uppercase tracking-[0.1em] sm:tracking-[0.14em] text-slate-500 leading-tight">{label}</p>
        <p className="text-base sm:text-2xl font-black text-slate-900 tracking-tight font-display truncate">{value}</p>
        {sub && <p className="text-[9px] sm:text-[10px] text-slate-400 leading-tight truncate">{sub}</p>}
      </div>
    </CardContent>
  </Card>
);

export default function HostPanelTab() {
  const [period, setPeriod] = useState('week');
  const [tab, setTab] = useState('dados');
  const [data, setData] = useState(null);
  const [starData, setStarData] = useState(null);
  const [starError, setStarError] = useState('');
  const [promo, setPromo] = useState(null);
  const [notices, setNotices] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const token = () => localStorage.getItem('customer_token');

  const load = useCallback(async (p) => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${BACKEND_URL}/api/host-panel/my-stats?period=${p}`,
        { headers: { Authorization: `Bearer ${token()}` } });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Erro ao carregar seus dados.');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load(period);
  }, [period, load]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/host-panel/my-star-host`,
      { headers: { Authorization: `Bearer ${token()}` } })
      .then((res) => { setStarData(res.data); setStarError(''); })
      .catch((e) => {
        setStarData(null);
        setStarError(e.response?.data?.detail || 'Dados de Star Host indisponíveis agora.');
      });
    axios.get(`${BACKEND_URL}/api/host-panel/my-promotion`,
      { headers: { Authorization: `Bearer ${token()}` } })
      .then((res) => setPromo(res.data))
      .catch(() => setPromo(null));
    axios.get(`${BACKEND_URL}/api/host-panel/notices`,
      { headers: { Authorization: `Bearer ${token()}` } })
      .then((res) => setNotices(res.data.notices || []))
      .catch(() => setNotices([]));
  }, []);

  const s = data?.stats || {};
  const p = data?.profile || {};

  return (
    <div data-testid="host-dashboard">
      <div className="space-y-5">

        {/* Abas principais */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="h-auto rounded-full bg-white/70 backdrop-blur-xl border border-white/60 p-1 shadow-sm gap-1 w-full sm:w-auto">
            <TabsTrigger value="dados" data-testid="host-tab-dados"
              className="rounded-full py-2 px-4 text-xs sm:text-sm font-semibold gap-1.5 text-slate-600 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-400 data-[state=active]:to-orange-600 data-[state=active]:text-white data-[state=active]:shadow-md flex-1 sm:flex-none">
              <TrendingUp className="w-4 h-4" /> Meus Dados
            </TabsTrigger>
            <TabsTrigger value="eventos" data-testid="host-tab-eventos"
              className="rounded-full py-2 px-4 text-xs sm:text-sm font-semibold gap-1.5 text-slate-600 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-400 data-[state=active]:to-orange-600 data-[state=active]:text-white data-[state=active]:shadow-md flex-1 sm:flex-none">
              <Briefcase className="w-4 h-4" /> Eventos
            </TabsTrigger>
            <TabsTrigger value="avisos" data-testid="host-tab-avisos"
              className="rounded-full py-2 px-4 text-xs sm:text-sm font-semibold gap-1.5 text-slate-600 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-400 data-[state=active]:to-orange-600 data-[state=active]:text-white data-[state=active]:shadow-md flex-1 sm:flex-none">
              <Megaphone className="w-4 h-4" /> Avisos
              {notices?.length > 0 && (
                <span className="ml-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[9px] font-black text-white">
                  {notices.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="politicas" data-testid="host-tab-politicas"
              className="rounded-full py-2 px-4 text-xs sm:text-sm font-semibold gap-1.5 text-slate-600 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-400 data-[state=active]:to-orange-600 data-[state=active]:text-white data-[state=active]:shadow-md flex-1 sm:flex-none">
              <BookOpen className="w-4 h-4" /> Políticas
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {tab === 'dados' && (
        <>
        {/* Período */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <Tabs value={period} onValueChange={setPeriod}>
            <TabsList className="h-auto rounded-full bg-white/70 backdrop-blur-xl border border-white/60 p-1 shadow-sm gap-1">
              {[['today', 'Hoje'], ['yesterday', 'Ontem'], ['week', 'Esta Semana']].map(([v, l]) => (
                <TabsTrigger key={v} value={v} data-testid={`host-period-${v}`}
                  className="rounded-full py-1.5 px-3.5 text-xs font-semibold text-slate-600 data-[state=active]:bg-white data-[state=active]:text-orange-600 data-[state=active]:shadow-sm">
                  {l}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <div className="flex items-center gap-2">
            {data && <span className="text-slate-400 text-xs font-medium">{data.start_date} — {data.end_date}</span>}
            <Button size="sm" variant="ghost"
              className="rounded-full text-slate-500 hover:text-orange-600 hover:bg-orange-50"
              onClick={() => load(period)} data-testid="host-refresh-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {error && (
          <div className="text-sm text-rose-800 bg-rose-50 border border-rose-200 rounded-xl p-3 shadow-sm" data-testid="host-stats-error">
            {error}
          </div>
        )}

        {loading && !data ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-orange-500 animate-spin" /></div>
        ) : (
          <div className="grid grid-cols-2 gap-2.5 sm:gap-3.5">
            <StatCard icon={Gift} label="Diamantes de presentes" value={fmtNum(s.gift_diamond)}
              accent="from-pink-500 to-rose-600 shadow-pink-200" testId="host-stat-gift" />
            <StatCard icon={Clover} label="Diamantes de sorte" value={fmtNum(s.luck_diamond)}
              accent="from-emerald-500 to-teal-600 shadow-emerald-200" testId="host-stat-luck" />
            <StatCard icon={Clock} label="Horas válidas de vídeo" value={fmtMinutes(s.valid_duration)}
              sub={s.valid_audio_duration > 0 ? `+ ${fmtMinutes(s.valid_audio_duration)} de áudio (não conta p/ Star Host)` : null}
              accent="from-orange-500 to-amber-500 shadow-orange-200" testId="host-stat-hours" />
            <StatCard icon={CalendarDays} label="Dias de live no período" value={fmtNum(s.live_days)}
              accent="from-purple-500 to-indigo-600 shadow-purple-200" testId="host-stat-days" />
          </div>
        )}

        <HostStarPanel starData={starData} promo={promo} error={starError} />

        <p className="text-center text-slate-400 text-xs">
          Estes são os seus dados pessoais na Fox Agency. Em breve: treinamentos e novidades. 🦊
        </p>
        </>
        )}

        {/* Eventos da Agência (exclusivo hosts) */}
        {tab === 'eventos' && (
        <div className="space-y-3" data-testid="host-events-section">
          <h2 className="text-lg sm:text-xl font-bold tracking-tight text-slate-800 font-display flex items-center gap-2">
            <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-sm">
              <Briefcase className="w-4 h-4 text-white" />
            </span>
            Eventos da Fox Agency
          </h2>
          {p.kako_id ? (
            <ClientHostEvents kakoId={p.kako_id} />
          ) : (
            <div className="flex justify-center py-10"><Loader2 className="w-7 h-7 text-orange-500 animate-spin" /></div>
          )}
        </div>
        )}

        {/* Avisos da Administração */}
        {tab === 'avisos' && (
        <div className="space-y-3" data-testid="host-notices-section">
          <h2 className="text-lg sm:text-xl font-bold tracking-tight text-slate-800 font-display flex items-center gap-2">
            <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-rose-400 to-orange-500 flex items-center justify-center shadow-sm">
              <Megaphone className="w-4 h-4 text-white" />
            </span>
            Avisos da Fox Agency
          </h2>
          {notices === null ? (
            <div className="flex justify-center py-10"><Loader2 className="w-7 h-7 text-orange-500 animate-spin" /></div>
          ) : notices.length === 0 ? (
            <p className="text-center text-sm text-slate-400 py-10" data-testid="host-notices-empty">
              Nenhum aviso por enquanto. Volte em breve! 🦊
            </p>
          ) : (
            <div className="space-y-3">
              {notices.map((n) => (
                <div key={n.id} data-testid={`host-notice-${n.id}`}
                  className={`bg-white/80 backdrop-blur-xl border shadow-[0_8px_32px_rgba(124,58,237,0.08)] rounded-2xl p-4 ${n.pinned ? 'border-amber-200' : 'border-white/60'}`}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-bold text-slate-800 text-sm sm:text-base font-display flex items-center gap-1.5">
                      {n.pinned && <Pin className="w-3.5 h-3.5 text-amber-500 shrink-0" />}
                      {n.title}
                    </p>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-0.5">{fmtNoticeDate(n.created_at)}</p>
                  <div className={`mt-2 ${NOTICE_HTML_CLS}`}
                    dangerouslySetInnerHTML={{ __html: n.content_html }} />
                </div>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Políticas */}
        {tab === 'politicas' && (
        <div className="space-y-3" data-testid="host-policies-section">
          <h2 className="text-lg sm:text-xl font-bold tracking-tight text-slate-800 font-display flex items-center gap-2">
            <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center shadow-sm">
              <FileText className="w-4 h-4 text-white" />
            </span>
            Políticas
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 sm:gap-3">
            {POLICIES.map((doc) => (
              <a key={doc.id} href={doc.file} target="_blank" rel="noopener noreferrer"
                data-testid={`host-policy-${doc.id}`}
                className="group block bg-white/80 backdrop-blur-xl border border-white/60 shadow-[0_8px_32px_rgba(124,58,237,0.08)] rounded-2xl p-4 hover:shadow-[0_12px_40px_rgba(249,115,22,0.14)] hover:-translate-y-0.5"
                style={{ transition: 'transform 0.2s ease, box-shadow 0.2s ease' }}>
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${doc.accent} shadow-md flex items-center justify-center mb-3`}>
                  <doc.icon className="w-5 h-5 text-white" />
                </div>
                <p className="text-slate-800 text-sm font-bold leading-snug font-display">{doc.title}</p>
                <p className="text-slate-500 text-xs mt-1">{doc.desc}</p>
                <span className="inline-flex items-center gap-1 text-orange-600 text-xs font-semibold mt-3 group-hover:underline">
                  Abrir documento <ExternalLink className="w-3 h-3" />
                </span>
              </a>
            ))}
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
